import {FieldUpdate, Inherited, Overridable} from '../core/types';
import {AuthPreset, AuthPresetSummary} from './authconfig';
import {Funds} from './funds';
import {ProviderStatus} from './providerstatus';

export enum CategoryType {
    Unknown = '',
    SkillBasedGames = 'skill-based-games',
    LuckyBasedGames = 'lucky-based-games',
    SteamSkins = 'steam-skins',
}

export interface CategorySummary {
    CategoryId: string;
    Type: CategoryType;
    Name: string;
}

export interface SiteTemplateSummary {
    Id: string;
    Name: string;
    CurrentVersion: string | null;
}

export interface RealmInfo {
    RealmId: string;
    CompanyId: string;
    Domain: string;
    DisplayName: string;
    IsActive: boolean;
    CreatedAt: string;
    Category: CategorySummary;
    SiteTemplate: SiteTemplateSummary | null;
    AuthPreset: AuthPresetSummary | null;
}

export interface CategoryKycConfig {
    RequireForDeposit: boolean;
    RequireForWithdrawal: boolean;
}

export interface CategorySumsubConfig {
    LevelName: string;
}

export interface CategoryCurrencyConfig {
    CreditsPerDollar: Funds;
    FreeCurrencyType: string;
    PaidCurrencyType: string;
    InitialFunds: Funds;
    ShowFreeCurrency: boolean;
    ShowPaidCurrency: boolean;
}

export interface ContentDocument {
    Title: string;
    Content: string;
}

export interface CategoryLegalConfig {
    TermsOfService: ContentDocument;
    PrivacyPolicy: ContentDocument;
    AmlPolicy: ContentDocument;
    CookiesPolicy: ContentDocument;
    RefundPolicy: ContentDocument;
    Disclaimer: ContentDocument;
    AboutDescription: ContentDocument;
    VirtualGoodsFulfilmentPolicy: ContentDocument;
}

export interface CategoryWithdrawConfig {
    WithdrawEnabled: boolean;
    MinWithdrawAmountUsd: number;
    WithdrawCreditsPerDollar: number;
}

export interface CategorySkinsConfig {
    SellMarginBasisPoints: number;
    SellPriceRiseToleranceBasisPoints: number;
    SellPriceDropToleranceBasisPoints: number;
}

export interface CategorySkinSellPriceRangeConfig {
    MinPriceCents: number | null;
    MaxPriceCents: number | null;
}

export type CategoryConfig =
    | { type: CategoryType.LuckyBasedGames; Kyc: CategoryKycConfig; Sumsub: CategorySumsubConfig; Currency: CategoryCurrencyConfig; Legal: CategoryLegalConfig }
    | { type: CategoryType.SkillBasedGames; Kyc: CategoryKycConfig; Sumsub: CategorySumsubConfig; Withdraw: CategoryWithdrawConfig; Currency: CategoryCurrencyConfig; Legal: CategoryLegalConfig }
    | { type: CategoryType.SteamSkins; Currency: CategoryCurrencyConfig; Skins: CategorySkinsConfig; SellPriceRange: CategorySkinSellPriceRangeConfig; Legal: CategoryLegalConfig };

export interface Category {
    CategoryId: string;
    Name: string;
    Config: CategoryConfig;
}

export interface RealmBrandingConfig {
    Name: string;
    FullName: string;
    Primary: string;
    Secondary: string;
    DisplayName: string;
    Description: string;
    FaviconUrl: string | null;
    OgImageUrl: string | null;
    LogoIconUrl: string | null;
}

export interface RealmSupportConfig {
    SupportEmail: string;
}

export interface RealmFineraDepositConfig {
    GroupId: string;
    ControlKey: string;
}

export interface RealmFineraConfig {
    IsEnabled: boolean;
    BaseUrl: string;
    Deposit: RealmFineraDepositConfig;
}

export enum PaymentMethod {
    Unknown = 0,
    None = 1,
    Finera = 2,
    Stripe = 3,
}

export interface CategoryLegalConfigSetup {
    TermsOfServiceOverride: ContentDocument | null;
    PrivacyPolicyOverride: ContentDocument | null;
    AmlPolicyOverride: ContentDocument | null;
    CookiesPolicyOverride: ContentDocument | null;
    RefundPolicyOverride: ContentDocument | null;
    DisclaimerOverride: ContentDocument | null;
    AboutDescriptionOverride: ContentDocument | null;
    VirtualGoodsFulfilmentPolicyOverride: ContentDocument | null;
}

export interface RealmFineraWithdrawConfig {
    GroupId: string;
    ControlKey: string;
    ConnectingPartyName: string;
    RsaPrivateKeyPem: string;
}

export interface RealmSteamConfig {
    WebApiKey: string;
}

export type RealmCategorySpecificConfigSetup =
    | { type: CategoryType.LuckyBasedGames; KycOverride: CategoryKycConfig | null; CurrencyOverride: CategoryCurrencyConfig | null; LegalOverride: CategoryLegalConfigSetup | null }
    | { type: CategoryType.SkillBasedGames; KycOverride: CategoryKycConfig | null; WithdrawOverride: CategoryWithdrawConfig | null; CurrencyOverride: CategoryCurrencyConfig | null; LegalOverride: CategoryLegalConfigSetup | null; FineraWithdraw: RealmFineraWithdrawConfig }
    | { type: CategoryType.SteamSkins; CurrencyOverride: CategoryCurrencyConfig | null; SkinsOverride: CategorySkinsConfig | null; SellPriceRangeOverride: CategorySkinSellPriceRangeConfig | null; LegalOverride: CategoryLegalConfigSetup | null; Steam: RealmSteamConfig };

export interface CategoryLegalConfigDetails {
    TermsOfService: Overridable<ContentDocument>;
    PrivacyPolicy: Overridable<ContentDocument>;
    AmlPolicy: Overridable<ContentDocument>;
    CookiesPolicy: Overridable<ContentDocument>;
    RefundPolicy: Overridable<ContentDocument>;
    Disclaimer: Overridable<ContentDocument>;
    AboutDescription: Overridable<ContentDocument>;
    VirtualGoodsFulfilmentPolicy: Overridable<ContentDocument>;
}

export type RealmCategorySpecificConfig =
    | { type: CategoryType.LuckyBasedGames; CategoryId: string; Name: string; Kyc: Overridable<CategoryKycConfig>; Sumsub: Inherited<CategorySumsubConfig>; Currency: Overridable<CategoryCurrencyConfig>; Legal: CategoryLegalConfigDetails }
    | { type: CategoryType.SkillBasedGames; CategoryId: string; Name: string; Kyc: Overridable<CategoryKycConfig>; Sumsub: Inherited<CategorySumsubConfig>; Withdraw: Overridable<CategoryWithdrawConfig>; Currency: Overridable<CategoryCurrencyConfig>; Legal: CategoryLegalConfigDetails; FineraWithdraw: RealmFineraWithdrawConfig }
    | { type: CategoryType.SteamSkins; CategoryId: string; Name: string; Currency: Overridable<CategoryCurrencyConfig>; Skins: Overridable<CategorySkinsConfig>; SellPriceRange: Overridable<CategorySkinSellPriceRangeConfig>; Legal: CategoryLegalConfigDetails; Steam: RealmSteamConfig };

export interface RealmConfig {
    RealmId: string;
    CompanyId: string;
    Version: number;
    Domain: string;
    IsActive: boolean;
    CreatedAt: string;
    Branding: RealmBrandingConfig;
    Support: RealmSupportConfig;
    AuthPreset: AuthPreset | null;
    Finera: RealmFineraConfig;
    ActivePaymentMethod: PaymentMethod;
    SiteTemplateId: string | null;
    CategorySpecific: RealmCategorySpecificConfig;
}

export interface SiteTemplate {
    SiteTemplateId: string;
    CategoryType: CategoryType;
    Name: string;
    Bucket: string;
    BasePrefix: string;
    CurrentVersion: string | null;
}

export interface CompanyStripeConfig {
    IsEnabled: boolean;
    SecretKey: string;
    PublishableKey: string;
    WebhookSecret: string;
}

export interface CompanyLiveChatConfig {
    GroupId: string | null;
}

export interface Company {
    Id: string;
    Name: string;
    Address: string;
    RegistrationDetails: string;
    CountryCode: string;
    Stripe: CompanyStripeConfig;
    CreatedAt: string;
    DeletedAt: string | null;
    LiveChatConfig: CompanyLiveChatConfig;
}

export interface RealmProviderStatus {
    RealmId: string;
    ProviderStatus: ProviderStatus;
}

export enum SiteFileDisposition {
    Unknown = '',
    Deny = 'deny',
    Immutable = 'immutable',
    Revalidate = 'revalidate',
    Html = 'html',
}

export interface RealmSummary {
    RealmId: string;
    CompanyId: string;
    Domain: string;
    DisplayName: string;
    IsActive: boolean;
    CreatedAt: string;
    ProviderStatus: ProviderStatus;
    Category: CategorySummary;
    SiteTemplate: SiteTemplateSummary | null;
    AuthPreset: AuthPresetSummary | null;
}

export type CategoryConfigPatch =
    | { type: CategoryType.LuckyBasedGames; Kyc: CategoryKycConfig | null; Sumsub: CategorySumsubConfig | null; Currency: CategoryCurrencyConfig | null; Legal: CategoryLegalConfig | null }
    | { type: CategoryType.SkillBasedGames; Kyc: CategoryKycConfig | null; Sumsub: CategorySumsubConfig | null; Withdraw: CategoryWithdrawConfig | null; Currency: CategoryCurrencyConfig | null; Legal: CategoryLegalConfig | null }
    | { type: CategoryType.SteamSkins; Currency: CategoryCurrencyConfig | null; Skins: CategorySkinsConfig | null; SellPriceRange: CategorySkinSellPriceRangeConfig | null; Legal: CategoryLegalConfig | null };

export interface CategoryUpdatePatch {
    Name: string | null;
    ConfigPatch: CategoryConfigPatch | null;
}

export interface CategoryLegalConfigUpdate {
    TermsOfServiceOverride: FieldUpdate<ContentDocument | null>;
    PrivacyPolicyOverride: FieldUpdate<ContentDocument | null>;
    AmlPolicyOverride: FieldUpdate<ContentDocument | null>;
    CookiesPolicyOverride: FieldUpdate<ContentDocument | null>;
    RefundPolicyOverride: FieldUpdate<ContentDocument | null>;
    DisclaimerOverride: FieldUpdate<ContentDocument | null>;
    AboutDescriptionOverride: FieldUpdate<ContentDocument | null>;
    VirtualGoodsFulfilmentPolicyOverride: FieldUpdate<ContentDocument | null>;
}

export type RealmCategorySpecificConfigUpdate =
    | { type: CategoryType.LuckyBasedGames; KycOverride: FieldUpdate<CategoryKycConfig | null>; CurrencyOverride: FieldUpdate<CategoryCurrencyConfig | null>; LegalOverride: CategoryLegalConfigUpdate | null }
    | { type: CategoryType.SkillBasedGames; KycOverride: FieldUpdate<CategoryKycConfig | null>; WithdrawOverride: FieldUpdate<CategoryWithdrawConfig | null>; CurrencyOverride: FieldUpdate<CategoryCurrencyConfig | null>; LegalOverride: CategoryLegalConfigUpdate | null; FineraWithdraw: RealmFineraWithdrawConfig | null }
    | { type: CategoryType.SteamSkins; CurrencyOverride: FieldUpdate<CategoryCurrencyConfig | null>; SkinsOverride: FieldUpdate<CategorySkinsConfig | null>; SellPriceRangeOverride: FieldUpdate<CategorySkinSellPriceRangeConfig | null>; LegalOverride: CategoryLegalConfigUpdate | null; Steam: RealmSteamConfig | null };

export interface SiteCacheRule {
    Pattern: string;
    Disposition: SiteFileDisposition;
}
